import React, { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { MOBILE_MAX_WIDTH } from "../../constants/breakpoints";
import { getClubByIdApi } from "../../api/api";
import ClubDetailDesktop from "./ClubDetailDesktop";
import ClubDetailMobile from "./ClubDetailMobile";
import { SedePending } from "../reservas/components/Sucursales";
import { useSedeActiva } from "../reservas/components/useSedeActiva";
import { FALLBACK_IMAGE } from "../reservas/components/ReservaShared";
import "../reservas/reservas.css";

const normalizeClubDetail = (response) => {
  const info = response?.data?.info || {};
  return {
    club: info.club || null,
    courts: Array.isArray(info.courts) ? info.courts : [],
    sucursales: Array.isArray(info.sucursales) ? info.sucursales : [],
    casaCentral: info.casaCentral || null,
  };
};

/**
 * Buyer club detail shell. Fetches the club by id and switches between the
 * desktop and mobile layouts, mirroring BarDetail.jsx. Availability/slot
 * selection and the hold/checkout wizard are wired in Unit 3
 * (useCourtReservationFlow) — this shell only renders club info, courts,
 * and a step-1 (date/duration/sport) entry point stub.
 */
const ClubDetail = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });

  const [club, setClub] = useState(null);
  const [courts, setCourts] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [casaCentral, setCasaCentral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!clubId) return undefined;

    const loadClub = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getClubByIdApi(clubId);
        if (!active) return;
        const normalized = normalizeClubDetail(response);
        setClub(normalized.club);
        setCourts(normalized.courts);
        setSucursales(normalized.sucursales);
        setCasaCentral(normalized.casaCentral);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "No pudimos encontrar el club.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadClub();
    return () => { active = false; };
  }, [clubId]);

  // Casa central: the sede in `?sucursal=` swaps the club's bookable content in place.
  const fetchSedeDetail = useCallback(async (ref) => {
    const normalized = normalizeClubDetail(await getClubByIdApi(ref));
    return normalized.club ? normalized : null;
  }, []);
  const { sedes, entry, status: sedeStatus } = useSedeActiva({ casa: club, sucursales, fetchDetail: fetchSedeDetail });

  if (loading) {
    return (
      <div className="reservas-root">
        <div className="wrap" style={{ textAlign: "center", padding: "80px 0" }}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="reservas-root">
        <div className="wrap">
          <Alert variant="danger">{error || "No pudimos encontrar el club."}</Alert>
          <button className="r-btn-soft" onClick={() => navigate("/canchas")}>
            Volver a canchas
          </button>
        </div>
      </div>
    );
  }

  // The chosen sede is still loading (or failed): casa central hero and Sucursales, no booking yet.
  if (sedeStatus) {
    return (
      <SedePending
        kind="canchas"
        hero={club}
        heroImage={club.image}
        fallbackImage={FALLBACK_IMAGE}
        sucursales={sucursales}
        sedes={sedes}
        status={sedeStatus}
        isMobile={isMobile}
        panelTitle="Reservá tu turno"
      />
    );
  }

  // On a sede the whole booking runs on that sede's club (its id reaches availability, holds,
  // reservations and reviews); the hero stays the casa central's. Remounting per sede resets the selection.
  const Layout = isMobile ? ClubDetailMobile : ClubDetailDesktop;
  const current = entry ? entry.data : { club, courts };
  return (
    <Layout
      key={String(current.club._id)}
      club={current.club}
      courts={current.courts}
      sucursales={sucursales}
      casaCentral={entry ? null : casaCentral}
      heroClub={entry ? club : null}
      sedes={sedes}
    />
  );
};

export default ClubDetail;
